/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import {
  Card,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Spacer,
  Heading,
  Icon,
  Badge,
  useToast,
  Box as CBox,
} from '@chakra-ui/react';
import { Progress } from '@chakra-ui/react';
import { FiFilter, FiFileText } from 'react-icons/fi';
import { FaFileExcel } from 'react-icons/fa6';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box, createTheme, ThemeProvider } from '@mui/material';
import { useExportReceivedBooksStaffMutation, useGetBookStaffs, useReceivedBooksStaff } from '@partiu/api/dashboard/hooks.ts';
import { IReceivedBookStaff } from '@partiu/api/dashboard/types.ts';
import { DataGridDateTimeFormatter, DataGridTextFormatter } from '../../../formatters.ts';
import { downloadExcel, CSVColumn } from '@partiu/ui/utils.ts';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider.tsx';
import { BookCard } from '../components';

const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('pt-BR').format(num);
};

const months: { [key: string]: string } = {
  '0': 'Janeiro',
  '1': 'Fevereiro',
  '2': 'Março',
  '3': 'Abril',
  '4': 'Maio',
  '5': 'Junho',
  '6': 'Julho',
  '7': 'Agosto',
  '8': 'Setembro',
  '9': 'Outubro',
  '10': 'Novembro',
  '11': 'Dezembro',
};

export function BooksDataTableTab() {
  const toast = useToast();
  const { bookstoreTypes } = useTheme();

  const hasBook = bookstoreTypes.includes('book');
  const hasAudiobook = bookstoreTypes.includes('audiobook');
  const hasMagazine = bookstoreTypes.includes('magazine');

  const [document, setDocument] = React.useState('');
  const [month, setMonth] = React.useState(String(new Date().getMonth()));
  const [year, setYear] = React.useState(String(new Date().getFullYear()));
  const [bookId, setBookId] = React.useState('');
  const [typeFilter, setTypeFilter] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);

  // Deselecionar livro quando mês ou ano mudar
  React.useEffect(() => {
    setBookId('');
    setCurrentPage(1);
  }, [month, year]);

  const { data, isLoading } = useReceivedBooksStaff({
    page: currentPage,
    page_size: 300,
    document: document.replaceAll('.', '').replaceAll('-', ''),
    month_reference: month ? month : '',
    year_reference: year || '',
    book: bookId ? bookId : '',
    book__type: typeFilter || ''
  });

  const extractPageFromUrl = (url: string | null | undefined): number | null => {
    if (!url) return null;
    const match = url.match(/[?&]page=(\d+)/);
    return match ? parseInt(match[1]) : null;
  };

  const { data: booksData } = useGetBookStaffs({
    month_featured: month ? month : '',
    month_featured_year: year || '',
    book__type: typeFilter || ''
  });

  const monthBooks = React.useMemo(() => {
    let books = booksData?.results || [];

    if (!hasBook) {
      books = books.filter((b: any) => b.type !== 'book');
    }
    if (!hasAudiobook) {
      books = books.filter((b: any) => b.type !== 'audiobook');
    }
    if (!hasMagazine) {
      books = books.filter((b: any) => b.type !== 'magazine');
    }
    return books;
  }, [booksData, hasBook, hasAudiobook, hasMagazine]);

  const exportColumns: CSVColumn[] = [
    { field: 'hashCode', headerName: 'Identificador Log' },
    { field: 'document', headerName: 'CPF' },
    { field: 'yearReference', headerName: 'Ano' },
    {
      field: 'monthReference',
      headerName: 'Mês',
      valueFormatter: (v: any) => (typeof v !== 'undefined' ? months[String(v)] : ''),
    },
    { field: 'bookTitle', headerName: 'Ebook' },
    { field: 'isbn', headerName: 'ISBN' },
    { field: 'author', headerName: 'Autor' },
    { field: 'description', headerName: 'Descrição' },
    {
      field: 'createdAt',
      headerName: 'Data',
      valueFormatter: (v: any) => (v ? new Date(v).toLocaleString('pt-BR') : ''),
    },
  ];

  const exportMutation = useExportReceivedBooksStaffMutation({
    async onSuccess(response) {
      await downloadExcel('relatorio-logs', [
        {
          name: 'Logs',
          headers: exportColumns,
          rows: (response?.results || []).map((r: IReceivedBookStaff) => ({
            ...r,
            hashCode: (r as any).hashCode || r.id,
            bookTitle: r.book?.title || '',
            isbn: r.book?.isbn || '',
            author: r.book?.author || '',
          })),
        },
      ]);
      toast({ title: 'Exportado', description: 'Arquivo Excel gerado com sucesso.', status: 'success' });
    },
    onError() {
      toast({ title: 'Erro', description: 'Não foi possível exportar.', status: 'error' });
    },
  });

  const handleExport = () => {
    exportMutation.mutate({
      document: document.replaceAll('.', '').replaceAll('-', ''),
      month_reference: month || '',
      year_reference: year || '',
      book__type: typeFilter || '',
      pagination: 'False',
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'hashCode',
      headerName: 'Identificador Log',
      width: 250,
      valueGetter: (params) => params.row.hashCode || params.row.id,
    },
    { field: 'document', headerName: 'CPF', width: 150, valueFormatter: DataGridTextFormatter },
    {
      field: 'monthReference', headerName: 'Mês', width: 100, valueFormatter:
        ({ value }) =>
          typeof value !== 'undefined' ? months[value] : ''
    },
    { field: 'yearReference', headerName: 'Ano', width: 100 },
    {
      field: 'bookTitle',
      headerName: 'Livro',
      flex: 1,
      maxWidth: 200,
      valueGetter: (params) => params.row.book?.title || '',
    },
    {
      field: 'isbn',
      headerName: 'ISBN',
      width: 140,
      valueGetter: (params) => params.row.book?.isbn || '',
    },
    { field: 'description', headerName: 'Descrição', flex: 1, minWidth: 200 },
    { field: 'createdAt', headerName: 'Data', width: 160, valueFormatter: DataGridDateTimeFormatter },
  ];

  return (
    <Box p={4}>
      {(isLoading || exportMutation.isLoading) && (
        <Progress size="xs" isIndeterminate position="sticky" top={0} zIndex={10} mb={2} />
      )}
      <Card width={'100%'} padding={'0px'} gap={5}>
        <CBox
          bg="gray.50"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
          p={4}
        >
          <HStack mb={3} spacing={2}>
            <Icon as={FiFilter} color="blue.500" />
            <Heading size="sm" color="gray.700">
              Filtros
            </Heading>
          </HStack>
          <Flex width={'100%'} gap={3} alignItems="flex-end" flexWrap="wrap">
            <FormControl width={{ base: '100%', lg: '220px' }}>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>
                CPF
              </FormLabel>
              <Input
                placeholder="Insira um CPF"
                value={document}
                onChange={(e) => {
                  const value = e.target.value;
                  setDocument(value);
                  if (value && value.length > 0) {
                    setMonth('');
                    setYear('');
                  }
                }}
                bg="white"
                variant="filled"
              />
            </FormControl>
            <FormControl width={{ base: '100%', lg: '140px' }}>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>
                Ano
              </FormLabel>
              <Select
                value={year}
                onChange={(e) => setYear(e.target.value)}
                bg="white"
                fontWeight={year === '' ? 'bold' : 'normal'}
              >
                <option value="" style={{ fontWeight: 'bold' }}>Total</option>
                {Array.from({ length: 8 }).map((_, idx) => {
                  const y = new Date().getFullYear() - idx;
                  return (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  );
                })}
              </Select>
            </FormControl>
            <FormControl width={{ base: '100%', lg: '160px' }}>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>
                Mês
              </FormLabel>
              <Select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                bg="white"
                fontWeight={month === '' ? 'bold' : 'normal'}
              >
                <option value="" style={{ fontWeight: 'bold' }}>Total</option>
                {Object.keys(months).map((m) => (
                  <option key={m} value={m}>
                    {months[m]}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl width={{ base: '100%', lg: '200px' }}>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>
                Tipo de mídia
              </FormLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                bg="white"
                fontWeight={typeFilter === '' ? 'bold' : 'normal'}
              >
                <option value="" style={{ fontWeight: 'bold' }}>Total</option>
                <option value="book">Ebook</option>
                <option value="audiobook">Audiobook</option>
                <option value="magazine">Revista</option>
                <option value="magazine-partiu">Revista Partiu</option>
              </Select>
            </FormControl>
            <Spacer />
            <Button
              onClick={handleExport}
              isLoading={exportMutation.isLoading}
              colorScheme="green"
              leftIcon={<FaFileExcel />}
              width={{ base: '100%', lg: 'auto' }}
            >
              Exportar Excel
            </Button>
          </Flex>
        </CBox>

        {/* <CBox width={'100%'}>
          <HStack spacing={2} overflowX={'auto'} paddingBottom={2} alignItems={'stretch'}>
            {monthBooks.sort((a: any, b: any) => b.levels.length - a.levels.length).sort((a: any, b: any) => {
              if (a.type === b.type) return 0;
              if (a.type === 'book') return -1;
              if (b.type === 'book') return 1;
              if (a.type === 'magazine') return -1;
              if (b.type === 'magazine') return 1;
              return 0;
            }).map((b: any) => (
              <CBox
                key={b.id}
                onClick={() => setBookId(String(b.id))}
                title={b.title}
                cursor={'pointer'}
                border={bookId === String(b.id) ? '2px solid #3182ce' : '1px solid #e2e8f0'}
                borderRadius={8}
                flexShrink={0}
                position={'relative'}
                _hover={{
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s'
                }}
              >
                <BookCard
                  book={b}
                  size="sm"
                  showAuthor={false}
                  showPages={false}
                />
              </CBox>
            ))}
          </HStack>
        </CBox> */}

        {(() => {
          const MuiTheme = createTheme({
            palette: { mode: 'light' },
          });
          return (
            <ThemeProvider theme={MuiTheme}>
              <CBox width={'100%'}>
                <HStack px={1} pb={2} spacing={2} alignItems="center">
                  <Icon as={FiFileText} color="blue.500" />
                  <Heading size="sm" color="gray.700">
                    Tabela de Logs
                  </Heading>
                  <Badge colorScheme="blue" borderRadius="full" px={2}>
                    {formatNumber(data?.count ?? 0)} registros
                  </Badge>
                </HStack>
                <Box width={'100%'} height="70vh">
                  <DataGrid
                    loading={isLoading}
                    rows={(data?.results || [])
                      .map((r: IReceivedBookStaff) => ({ ...r, bookTitle: r.book?.title }))}
                    columns={columns}
                    pageSizeOptions={[15]}
                    rowHeight={42}
                    hideFooter
                  />
                </Box>
                <HStack spacing={2} justifyContent="space-between" padding={4}>
                  <Button
                    onClick={() => {
                      const prevPage = extractPageFromUrl(data?.previous);
                      if (prevPage !== null) {
                        setCurrentPage(prevPage);
                      }
                    }}
                    isDisabled={!data?.previous}
                    colorScheme="blue"
                    variant="outline"
                    isLoading={isLoading}
                  >
                    ← Anterior
                  </Button>
                  <Spacer />
                  <Button
                    onClick={() => {
                      const nextPage = extractPageFromUrl(data?.next);
                      if (nextPage !== null) {
                        setCurrentPage(nextPage);
                      }
                    }}
                    isDisabled={!data?.next}
                    colorScheme="blue"
                    variant="outline"
                    isLoading={isLoading}
                  >
                    Próximo →
                  </Button>
                </HStack>
              </CBox>
            </ThemeProvider>
          );
        })()}
      </Card>
    </Box>
  );
}
